import {
  DocumentReferenceAttachment,
  PAGE_NUMBER_EXTENSION_URL,
} from '../../AttachmentArbiter/Attachment';
import { createRenderingTracker, trackingKey } from '../createRenderingTracker';

/**
 * Why a tracker for document references?
 * Clinicians and agents both tend to cite the same source PDF more than
 * once across an impression's narrative. Without dedup the reader sees the
 * same expansion panel / thumbnail repeated for each citation, which is
 * the single most common visible duplicate in the report UI.
 *
 * Why include the page number in the key?
 * The same PDF cited at page 1 and page 5 is two genuinely different views
 * of the source document. Treating only the resource id as identity would
 * collapse those into one widget and hide useful information; treating
 * `${resource}|page-N` as identity dedupes "the same page of the same
 * doc" while still letting different pages render side-by-side.
 */
export type DocumentReferenceTrackerInput = {
  attachment: DocumentReferenceAttachment;
  reference: fhir.Reference;
};

const getReferencePageNumber = (reference: fhir.Reference): number =>
  reference.extension?.find(ext => ext.url === PAGE_NUMBER_EXTENSION_URL)
    ?.valueInteger ?? 1;

const getDocumentReferenceKey = ({
  attachment,
  reference,
}: DocumentReferenceTrackerInput) =>
  trackingKey(
    `${attachment.identifier}|page-${getReferencePageNumber(reference)}`,
  );

const handle = createRenderingTracker<DocumentReferenceTrackerInput>(
  getDocumentReferenceKey,
);

export const DocumentReferenceTrackingProvider = handle.Provider;
export const useDocumentReferenceTracker = handle.useTracker;
